/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */
import * as THREE from 'three'

// @ts-ignore
import { DeviceOrientationControls } from './DeviceOrientationControls'
import { ARViewerWeb } from '.'

// HACK: Store THREE on the window
window.THREE = THREE

// Attach skeleton utils, used for cloning 3D objects with animationrequire('./SkeletonUtils.ts')

/** Responsible for rendering 3D content */
export default class Render3DPlugin {
  ar: ARViewerWeb
  fps: number
  lastFPSCheck: number
  numFramesRendered: number
  canvas: HTMLCanvasElement
  clock: any
  renderer: any
  camera: any
  cameraControl: any
  scene: any
  static skyboxPromise: any

  constructor(ar: ARViewerWeb) {
    // Store AR
    this.ar = ar
    this.canvas = document.createElement('canvas')
    this.canvas.style.cssText =
      'position: absolute; z-index: 2; top: 0px; left: 0px; width: 100%; height: 100%;'

    // Frames per second count
    this.fps = 0
    this.lastFPSCheck = Date.now()
    this.numFramesRendered = 0
  }

  get name() {
    return 'Render3DPlugin'
  }

  async load(div: HTMLDivElement) {
    // Create canvas element
    div.appendChild(this.canvas)

    // Add click handler to canvas
    this.canvas.addEventListener('mousedown', this.onClick.bind(this))
    this.canvas.addEventListener('touchstart', this.onClick.bind(this))

    // Create clock to measure delta between frames
    this.clock = new THREE.Clock()

    // Prepare 3D
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      alpha: true,
      antialias: true
    })
    this.renderer.setClearColor(0, 0)
    this.renderer.setPixelRatio(window.devicePixelRatio || 1)
    this.renderer.gammaOutput = true
    this.renderer.gammaFactor = 2.2
    this.ar.renderer = this.renderer

    // Setup camera
    this.camera = new THREE.PerspectiveCamera(45, 640 / 480, 0.0001, 20000)
    this.camera.position.y = 4
    // @ts-ignore
    this.cameraControl = new DeviceOrientationControls(this.camera)
    this.ar.camera = this.camera

    // Setup scene, put it on the AR class so other plugins can access it
    this.scene = new THREE.Scene()
    this.scene.add(this.camera)
    this.ar.scene = this.scene

    // Setup ambient lighting
    // const light = new THREE.AmbientLight(0x404040);
    // this.scene.add(light);

    // Setup environment map
    this.scene.environment = await Render3DPlugin.loadSkyboxCubeMap()

    // Setup audio listener
    this.ar.audioListener = new THREE.AudioListener()
    this.camera.add(this.ar.audioListener)

    // Setup resize handler
    this.onResize = this.onResize.bind(this)
    window.addEventListener('resize', this.onResize)
    this.onResize()

    // Bind render function
    this.render3D = this.render3D.bind(this)

    // Start render loop
    this.render3D()
  }

  /** @private Loads the skybox cubemap texture */
  static loadSkyboxCubeMap() {
    // Stop if loading already
    if (Render3DPlugin.skyboxPromise) return Render3DPlugin.skyboxPromise

    // Create URLs
    const cubeMapURLs = [
      require('libs/sdk/react/src/faces/Faces/ThreeDFace/envmap_px.jpg'),
      require('libs/sdk/react/src/faces/Faces/ThreeDFace/envmap_nx.jpg'),
      require('libs/sdk/react/src/faces/Faces/ThreeDFace/envmap_py.jpg'),
      require('libs/sdk/react/src/faces/Faces/ThreeDFace/envmap_ny.jpg'),
      require('libs/sdk/react/src/faces/Faces/ThreeDFace/envmap_pz.jpg'),
      require('libs/sdk/react/src/faces/Faces/ThreeDFace/envmap_nz.jpg')
    ]

    // Create loader
    const envMap = new THREE.CubeTextureLoader().load(cubeMapURLs)
    envMap.format = THREE.RGBAFormat
    Render3DPlugin.skyboxPromise = Promise.resolve(envMap)

    // Return promise
    return Render3DPlugin.skyboxPromise
  }

  unload() {
    // Remove 3D renderer
    this.renderer = null

    // Remove listeners
    window.removeEventListener('resize', this.onResize)
  }

  /** @private The render loop */
  render3D() {
    // Stop render loop if no renderer
    if (!this.renderer) return

    // Do again soon
    requestAnimationFrame(this.render3D)

    // Get delta time
    const delta = Math.min(this.clock.getDelta(), 0.5)

    // Stop if still loading or something has gone wrong
    if (this.ar.state.isLoading || this.ar.state.error) return

    // Notify any interested plugins, stop if any of them want us to skip this frame
    let shouldSkip = false
    for (const plugin of this.ar.plugins) {
      if (plugin.onRender3D) {
        if (plugin.onRender3D(delta, this.scene, this.camera) === false) shouldSkip = true
      }
    }

    if (shouldSkip) return

    // Count FPS
    this.numFramesRendered += 1
    if (this.lastFPSCheck + 1000 < Date.now()) {
      this.lastFPSCheck = Date.now()
      this.fps = this.numFramesRendered
      this.numFramesRendered = 0
    }

    // Update camera position based on controller
    this.cameraControl.update()

    // Do render
    this.renderer.render(this.scene, this.camera)
  }

  /** @private Called when the view is resized */
  onResize() {
    // Get size of canvas
    const rect = this.canvas!.getBoundingClientRect()

    // Update renderer size
    this.renderer.setSize(rect.width, rect.height, false)
    this.camera.aspect = rect.width / rect.height
    this.camera.updateProjectionMatrix()
  }

  // Show debug info
  debugStats() {
    return `FPS: ${this.fps}`
  }

  /** Called when the user clicks on the canvas */
  onClick(e: any) {
    e.preventDefault()

    // Get position of touch on the page
    let x = e.changedTouches?.[0].pageX || e.pageX || 0
    let y = e.changedTouches?.[0].pageY || e.pageY || 0

    // Calculate position of touch within the canvas element
    const rect = this.canvas.getBoundingClientRect()
    x -= rect.left
    y -= rect.top

    // Normalize the coordinates from pixels to (-1 to 1)
    x = (x / rect.width) * 2 - 1
    y = (y / rect.height) * 2 - 1
    y *= -1

    // Raytrace to find the touched object
    const raycaster = new THREE.Raycaster()
    const mouse = new THREE.Vector2(x, y)
    raycaster.setFromCamera(mouse, this.camera)
    const intersects = raycaster.intersectObject(this.scene, true)
    console.log('touch', x, y, intersects)
    if (intersects.length === 0) return

    // Walk up the chain until we find an object with an .onClick handler
    let obj: any = intersects[0].object
    while (obj) {
      // Check for handler
      if (obj.onClick) {
        // Call it
        obj.onClick(e)
        break
      } else {
        // No handler, keep walking
        obj = obj.parent!
      }
    }
  }
}
